import { isMagicSfx, type MagicSfx } from "@greg-chick/shared";

/**
 * 🔊 **마법 소리의 알맹이** — 무엇처럼 들려야 하나, 얼마나 크게, 언제 안 나나.
 *
 * ## 🔴 왜 알맹이만 따로 두나
 * 이 소리를 내는 화면이 **셋**이다: 책 뷰어 · 카메라 AR · 관리자 미리보기. 운영자가 고른 소리와
 * 아이가 듣는 소리가 갈리면 **눈에 안 보여서 더 오래 안 들킨다**(그림·자리에서 이미 겪은 갈림).
 * 그런데 「소리 파일을 어디서 찾나」는 앱마다 다르다(아이 화면과 관리자는 매니페스트를 다르게
 * 읽는다). 그래서 **찾는 일만 밖에서 받고**, 나는 소리 자체는 여기 한 곳에 둔다.
 * `packages/shared` 에는 못 둔다 — 거기는 DOM 금지(서버와 같이 쓰는 코드)다.
 *
 * ## 🔴 자동재생이 아니다
 * 불변식 6이 막는 것은 **저절로 나는 소리**다. 여기 소리는 전부 아이 손가락이 만든다 —
 * 누르지 않으면 한 번도 안 난다. 그래서 기본이 켜짐이고, 끄는 스위치를 마법 바에 둔다.
 * 월드 앰비언스(`gc_sound_v1`)와는 **다른 자리**를 쓴다: 그건 계속 흐르는 소리라 기본이 꺼짐이다.
 * 하나로 묶으면 한쪽은 늘 틀린 기본값을 갖는다.
 *
 * ## 조용히 실패한다
 * 소리는 **곁들이는 것**이다. 오디오를 못 열어도(정책·기기·사생활 창) 마법은 그대로 돌아야
 * 하므로 던지지 않는다. 이건 「의도된 경계」다 — 실패가 화면을 멈추면 본말이 뒤집힌다.
 */

const PREF_KEY = "gc_magic_sfx_v1";

/** 마법 소리를 낼까 — **기본은 켜짐**(아이가 누른 것에 대한 대답이라). */
export function magicSfxOn(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) !== "0";
  } catch {
    return true; // 저장소를 못 읽어도 소리는 난다
  }
}

export function setMagicSfxOn(on: boolean): void {
  try {
    localStorage.setItem(PREF_KEY, on ? "1" : "0");
  } catch {
    /* 사생활 보호 창 — 이번 판에만 안 남는다 */
  }
}

/** 🔴 소리 크기 상한. 아이 귀에 가까운 기기라 작게 — 놀라게 하지 않는다. */
const VOLUME: Record<MagicSfx, number> = {
  tap: 0.22,
  bite: 0.3,
  sparkle: 0.26,
  slide: 0.24,
  water: 0.26,
  light: 0.24,
  leaf: 0.2,
};

/**
 * 그 자리에서 만드는 소리 — 파일이 아직 없을 때.
 *
 * 짧은 음 하나(또는 잡음 한 자락)에 **빠른 감쇠**를 씌운다. 길게 끌면 그림책 분위기를 깬다.
 * 소리마다 「무엇처럼 들려야 하나」를 적어 둔다 — 나중에 진짜 소리로 바꿀 때 기준이 된다.
 */
const RECIPE: Record<MagicSfx, { kind: "tone" | "noise"; from: number; to: number; ms: number; like: string }> = {
  tap: { kind: "tone", from: 660, to: 540, ms: 110, like: "나무 실로폰을 살짝 친 소리" },
  bite: { kind: "noise", from: 2400, to: 700, ms: 180, like: "바삭한 빵을 베어 무는 소리" },
  sparkle: { kind: "tone", from: 1180, to: 1760, ms: 260, like: "별이 반짝 올라가는 소리" },
  slide: { kind: "noise", from: 900, to: 260, ms: 300, like: "종이·나무가 스르륵 밀리는 소리" },
  water: { kind: "tone", from: 420, to: 300, ms: 320, like: "물이 찰랑이는 소리" },
  light: { kind: "tone", from: 300, to: 620, ms: 380, like: "등에 불이 들어오는 소리" },
  leaf: { kind: "noise", from: 1600, to: 1100, ms: 260, like: "잎이 사르락 스치는 소리" },
};

let ctx: AudioContext | null = null;
function audio(): AudioContext | null {
  try {
    const C = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return null;
    ctx ??= new C();
    // 누르기 전엔 막혀 있을 수 있다 — 손가락이 닿은 이 순간에 깨운다
    if (ctx.state === "suspended") void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

function synth(name: MagicSfx): void {
  const ac = audio();
  if (!ac) return;
  const r = RECIPE[name];
  const t0 = ac.currentTime;
  const dur = r.ms / 1000;
  const gain = ac.createGain();
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(VOLUME[name], t0 + 0.012); // 딸깍 소리가 안 나게 살짝 띄운다
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  gain.connect(ac.destination);

  if (r.kind === "tone") {
    const osc = ac.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(r.from, t0);
    osc.frequency.exponentialRampToValueAtTime(r.to, t0 + dur);
    osc.connect(gain);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
    return;
  }
  // 잡음 한 자락을 좁은 띠로 걸러 「사각」·「스르륵」으로 만든다
  const n = Math.max(1, Math.floor(ac.sampleRate * dur));
  const buf = ac.createBuffer(1, n, ac.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
  const src = ac.createBufferSource();
  src.buffer = buf;
  const bp = ac.createBiquadFilter();
  bp.type = "bandpass";
  bp.Q.value = 1.2;
  bp.frequency.setValueAtTime(r.from, t0);
  bp.frequency.exponentialRampToValueAtTime(r.to, t0 + dur);
  src.connect(bp);
  bp.connect(gain);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

/** 같은 소리가 겹쳐 터지지 않게 — 아이는 연타한다. */
const lastAt = new Map<string, number>();

/** 이 이름이 어떤 소리여야 하는지 — 진짜 소리로 바꿀 때의 기준. 관리자가 그대로 보여 준다. */
export function magicSfxLike(name: MagicSfx): string {
  return RECIPE[name].like;
}

/**
 * 소리 한 번. 이름이 없거나 모르는 이름이면 아무 일도 안 한다.
 *
 * @param findUrl 게시된 소리 파일을 찾는 일 — **앱마다 다르다**(아이 화면/관리자).
 *                주소를 돌려주면 그 파일을, 없으면 **그 자리에서 만든 소리**를 낸다.
 *                「더미로 먼저, 에셋은 교체만」(CLAUDE.md) — 소리를 올릴 때까지 기능이
 *                잠들어 있으면 「만들어 놓고 닿을 길이 없다」가 또 난다.
 */
export function playSfxWith(name: unknown, findUrl?: (n: MagicSfx) => string | undefined): void {
  if (!isMagicSfx(name) || !magicSfxOn()) return;
  const now = Date.now();
  if (now - (lastAt.get(name) ?? 0) < 70) return; // 연타 방어 — 아이는 연타한다
  lastAt.set(name, now);
  const url = findUrl?.(name);
  if (url) {
    try {
      const el = new Audio(url);
      el.volume = VOLUME[name];
      void el.play().catch(() => synth(name)); // 파일이 막히면 만든 소리로
      return;
    } catch {
      /* 아래로 */
    }
  }
  synth(name);
}

/** 검사용 — 연타 기억을 비운다. */
export function resetSfxThrottle(): void {
  lastAt.clear();
}
